import { useEffect, useRef, useState } from "react";
import { Reveal } from "@/components/Reveal";
import { API } from "@/assets";
import { LicenseModal, TIERS } from "@/components/LicenseModal";
import { Play, Pause, Volume2, VolumeX, ArrowUpRight, Loader2, BadgeDollarSign, Crown } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmt = (s) => {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

const mediaUrl = (url) => (url && url.startsWith("http") ? url : `${BACKEND}${url}`);

export const AudioShowcase = ({ onQuote }) => {
  const [tracks, setTracks] = useState([]);
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.85);
  const [muted, setMuted] = useState(false);
  const [licenseTrack, setLicenseTrack] = useState(null);
  const [buying, setBuying] = useState(false);
  const audioRef = useRef(null);
  const seekRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/tracks`)
      .then((r) => r.json())
      .then((d) => setTracks(d.tracks || []))
      .catch(() => setTracks([]));
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = muted ? 0 : volume;
    }
  }, [volume, muted]);

  const playTrack = (track) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (current?.id === track.id) {
      if (playing) {
        audio.pause();
        setPlaying(false);
      } else {
        audio.play();
        setPlaying(true);
      }
      return;
    }
    setCurrent(track);
    setProgress(0);
    audio.src = mediaUrl(track.file);
    audio.play();
    setPlaying(true);
  };

  const onSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = seekRef.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * duration;
    setProgress(ratio * duration);
  };

  const handleBuy = async (track, tier) => {
    setBuying(true);
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lookup_key: tier.lookupKey,
          track_id: track.id,
          origin_url: window.location.origin,
        }),
      });
      if (!res.ok) throw new Error("checkout failed");
      const { checkout_url } = await res.json();
      window.location.href = checkout_url;
    } catch {
      setBuying(false);
      setLicenseTrack(null);
      onQuote(
        "Beat Licensing",
        `Beat: "${track.title}" — ${tier.name} license at $${tier.price} USD. (Online checkout was unavailable, please invoice me.)`
      );
    }
  };

  const handleRequestQuote = (track, tier) => {
    setLicenseTrack(null);
    onQuote(
      "Beat Licensing",
      `Beat: "${track.title}" (${track.bpm || "?"} BPM${track.key ? `, ${track.key}` : ""}) — ${tier.name} license at $${tier.price} USD. I've reviewed the licensing agreement and would like to proceed.`
    );
  };

  const renderTrack = (t, i) => {
    const isActive = current?.id === t.id;
    const isPlaying = isActive && playing;
    return (
      <Reveal key={t.id} delay={Math.min(i, 4) * 0.06} className="min-w-0">
        <div
          className={`group flex items-center gap-4 sm:gap-6 bg-charcoal border rounded-2xl p-4 sm:p-5 transition-all duration-400 ${
            isActive
              ? "border-venom/60 shadow-[0_0_40px_rgba(57,255,20,0.12)]"
              : "border-crystal/20 hover:border-crystal/50"
          }`}
          data-testid={`track-row-${t.id}`}
        >
          <div className="relative shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden border border-white/10">
            <img src={mediaUrl(t.cover)} alt={`${t.title} cover art`} loading="lazy" className="w-full h-full object-cover" />
            <button
              onClick={() => playTrack(t)}
              aria-label={isPlaying ? `Pause ${t.title}` : `Play ${t.title}`}
              className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${
                isActive ? "bg-obsidian/55" : "bg-obsidian/35 opacity-0 group-hover:opacity-100"
              }`}
              data-testid={`track-play-${t.id}`}
            >
              {isPlaying ? (
                <Pause size={22} className="text-venom" fill="currentColor" />
              ) : (
                <Play size={22} className="text-venom" fill="currentColor" />
              )}
            </button>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-3">
              <h3 className="font-display text-white text-sm sm:text-base font-bold truncate">{t.title}</h3>
              <span className="text-[11px] text-ice/50">{t.artist}</span>
            </div>
            <div className="mt-1.5 flex flex-wrap gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-venom border border-venom/50 rounded-full px-2.5 py-0.5" data-testid={`track-format-${t.id}`}>
                {(t.format || "WAV")} · from ${(t.format || "WAV") === "MP3" ? "29" : "59"}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ice/45 border border-white/20 rounded-full px-2.5 py-0.5">
                {t.preview ? "45-sec Preview" : "Preview"}
              </span>
              {[t.mood, t.bpm ? `${t.bpm} BPM` : null, t.key].filter(Boolean).map((tag) => (
                <span
                  key={tag}
                  className="font-mono text-[10px] uppercase tracking-[0.15em] text-crystal border border-crystal/40 rounded-full px-2.5 py-0.5"
                >
                  {tag}
                </span>
              ))}
            </div>
            {isActive && (
              <div className="mt-3 flex items-center gap-3">
                <span className="font-mono text-[10px] text-ice/50 w-8">{fmt(progress)}</span>
                <div
                  ref={seekRef}
                  onClick={onSeek}
                  className="relative flex-1 h-1.5 rounded-full bg-white/10 cursor-pointer"
                  data-testid="track-seek-bar"
                >
                  <div
                    className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-crystal to-venom"
                    style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }}
                  />
                </div>
                <span className="font-mono text-[10px] text-ice/50 w-8">{fmt(duration)}</span>
              </div>
            )}
          </div>

          <div className="hidden sm:flex items-end gap-[2.5px] h-8 w-16 shrink-0" aria-hidden="true">
            {[0.6, 1, 0.4, 0.85, 0.55, 0.95, 0.5, 0.75].map((h, j) => (
              <span
                key={j}
                className={`eq-bar flex-1 rounded-sm ${isPlaying ? "" : "eq-paused"} ${j % 2 ? "bg-crystal/80" : "bg-venom/80"}`}
                style={{ height: `${h * 100}%`, animationDelay: `${j * 0.09}s` }}
              />
            ))}
          </div>

          <div className="hidden md:flex items-center gap-2 shrink-0">
            <button
              onClick={() => setMuted(!muted)}
              className="text-ice/60 hover:text-venom transition-colors"
              aria-label={muted ? "Unmute" : "Mute"}
              data-testid="track-volume-toggle"
            >
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={muted ? 0 : volume}
              onChange={(e) => { setVolume(parseFloat(e.target.value)); setMuted(false); }}
              className="dge-range w-20"
              aria-label="Volume"
              data-testid="track-volume-slider"
            />
          </div>

          <button
            onClick={() => setLicenseTrack(t)}
            className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold border border-venom/50 text-venom rounded-full px-4 py-2 hover:bg-venom hover:text-obsidian active:scale-95 transition-all duration-300"
            data-testid={`track-license-${t.id}`}
          >
            License
            <ArrowUpRight size={14} />
          </button>
        </div>
      </Reveal>
    );
  };

  const nonExclusive = tracks.filter((t) => !t.exclusive);
  const exclusive = tracks.filter((t) => t.exclusive);

  return (
    <section id="sound" className="relative py-20 lg:py-28" data-testid="audio-showcase-section">
      <audio
        ref={audioRef}
        preload="metadata"
        onTimeUpdate={(e) => setProgress(e.target.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onEnded={() => setPlaying(false)}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-venom font-medium mb-4">
              Beat Store &amp; Licensing
            </p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight">
              Cold-Blooded
              <br />
              <span className="text-stroke-ice">Soundscapes™</span>
            </h2>
          </div>
          <p className="text-sm text-ice/55 max-w-xs leading-relaxed">
            Lab-grade instrumentals with instant delivery. Pick a tier, review the agreement, check out
            securely — your files land in your inbox in minutes.
          </p>
        </Reveal>

        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="license-tiers-row">
          {TIERS.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.07}>
              <div
                className={`relative h-full bg-charcoal rounded-xl p-4 sm:p-5 border transition-all duration-400 hover:-translate-y-1 ${
                  t.bestValue
                    ? "border-venom/60 hover:glow-venom"
                    : "border-crystal/25 hover:border-crystal/60 hover:glow-crystal"
                }`}
                data-testid={`tier-card-${t.id}`}
              >
                {t.bestValue && (
                  <span className="absolute -top-2.5 left-4 bg-venom text-obsidian font-mono text-[9px] uppercase tracking-[0.15em] font-semibold rounded-full px-2.5 py-0.5">
                    Best Value
                  </span>
                )}
                <div className="flex items-center gap-2">
                  <BadgeDollarSign size={15} className={t.bestValue ? "text-venom" : "text-crystal"} />
                  <p className="font-display text-white text-xs sm:text-sm font-bold">{t.name}</p>
                </div>
                <p className="mt-2 font-display text-xl sm:text-2xl font-bold text-venom">${t.price}</p>
                <p className="mt-1 text-[11px] sm:text-xs text-ice/55">{t.tagline}</p>
                <p className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-ice/35">{t.format}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {tracks.length === 0 ? (
          <div className="mt-14 flex items-center justify-center gap-3 text-ice/50" data-testid="tracks-loading">
            <Loader2 className="animate-spin" size={20} />
            <span className="font-mono text-xs uppercase tracking-[0.2em]">Loading tracks…</span>
          </div>
        ) : (
          <>
            <Reveal className="mt-14">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="nonexclusive-heading">
                <h3 className="font-display text-lg sm:text-xl font-bold text-white">
                  Non-Exclusive Beats
                </h3>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-venom border border-venom/40 rounded-full px-3 py-1 whitespace-nowrap">
                  Lease from $29
                </span>
                <div className="hidden sm:block h-px flex-1 bg-gradient-to-r from-venom/40 to-transparent" aria-hidden="true" />
              </div>
              <p className="mt-2 text-xs sm:text-sm text-ice/50 max-w-xl">
                Shared licenses at friendly prices — MP3, WAV or Unlimited. The beat stays in the store for others.
              </p>
            </Reveal>
            <div className="mt-6 grid gap-4" data-testid="nonexclusive-track-list">
              {nonExclusive.length === 0 ? (
                <p className="text-sm text-ice/40 py-6">No non-exclusive beats in the store right now.</p>
              ) : (
                nonExclusive.map(renderTrack)
              )}
            </div>

            {exclusive.length > 0 && (
              <>
                <Reveal className="mt-16">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="exclusive-heading">
                    <h3 className="font-display text-lg sm:text-xl font-bold text-white flex items-center gap-2.5">
                      <Crown size={20} className="text-crystal" />
                      Exclusive Beats
                    </h3>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#B983FF] border border-crystal/50 rounded-full px-3 py-1 whitespace-nowrap">
                      One owner · $499
                    </span>
                    <div className="hidden sm:block h-px flex-1 bg-gradient-to-r from-crystal/50 to-transparent" aria-hidden="true" />
                  </div>
                  <p className="mt-2 text-xs sm:text-sm text-ice/50 max-w-xl">
                    Sold once, then retired from the catalog forever. Full stems and exclusive use of the instrumental.
                  </p>
                </Reveal>
                <div className="mt-6 grid gap-4" data-testid="exclusive-track-list">
                  {exclusive.map(renderTrack)}
                </div>
              </>
            )}
          </>
        )}

        <Reveal delay={0.1}>
          <p className="mt-8 text-center text-[11px] font-mono uppercase tracking-[0.2em] text-ice/35">
            Instant download after secure checkout · Copyright © 2026 DGE: Cold-Blooded Records LLC™
          </p>
        </Reveal>
      </div>

      <LicenseModal
        track={licenseTrack}
        onClose={() => setLicenseTrack(null)}
        onBuy={handleBuy}
        onRequestQuote={handleRequestQuote}
        buying={buying}
      />
    </section>
  );
};
